import type { DataOption } from '@/entities/column'
import type { FilterRuleModel, PricingCampaignModel } from '@/shared/generated'
import type { TreeNodeType } from '@/shared/lib/tree'

import { useMemo } from 'react'
import { keys, last, map, pipe, split } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import {
	formatFilterRule,
	isFilterbaleField,
	prepareList,
	toAdaptedSchema,
} from '@/entities/column'
import { useSalesEntitiesQuery } from '@/entities/optimization-group'
import { useStoredFiltersKeys } from '@/entities/user'

import { getFieldConfig, QuickFiltersBar } from '@/widgets/quick-filters-bar'
import { SavedReportPicker } from '@/widgets/saved-report-picker'

import ProductsAssignActionsDropdownMenu from '@/shared/components/common/ProductsAssignActionsDropdownMenu'
import ProductsBulkActionsDropdownMenu from '@/shared/components/common/ProductsBulkActionsDropdownMenu'
import TableFilters from '@/shared/components/data-grid/TableFilters'
import { getFiledConfig } from '@/shared/components/refinement-bar/helpers'
import { Separator } from '@/shared/components/ui/separator'
import { OptimizationStatus, SavedFilterType } from '@/shared/generated'
import {
	useCanEditModelInput,
	useOptimizationStatus,
} from '@/shared/hooks/data'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { ampli } from '@/shared/lib/ampli'
import { useStore } from '@/shared/lib/datasheet/store'
import { useModals } from '@/shared/lib/modal-manager'
import { getTree } from '@/shared/lib/tree'
import { encodeComplexQuery } from '@/shared/tools/location'

type ProductsManagerProps = {
	onAssignToPricingCampaign(
		pricingCampaign: PricingCampaignModel | string
	): void
	onCreatePricingCampaign(stat: object): void
	onOpenBulkModal(): void
	onOpenImportModal(): void
	dataChoices: Record<string, DataOption[] | TreeNodeType[]>
}
export default function ProductsToolbar({
	dataChoices,
	onOpenBulkModal,
	onAssignToPricingCampaign,
	onCreatePricingCampaign,
	onOpenImportModal,
}: ProductsManagerProps) {
	const { t } = useTranslation()
	const schema = useStore((state) => state.schema)
	const { showModal } = useModals()
	const optimizationGroupId = useOptimizationGroupId()!
	const rowSelection = useStore((state) => state.rowSelection)
	const setRowSelection = useStore((state) => state.setRowSelection)

	const { data: posIds } = useSalesEntitiesQuery({
		select: (data) => {
			const posIdsField = schema?.find((field) => field.id === 'pos_ids')
			if (!posIdsField) return []
			const list = prepareList(data?.sales_entities, posIdsField)

			return getTree(list)
		},
	})

	const [storedQuickFiltersKeys, saveStoredQuickFiltersKeys] =
		useStoredFiltersKeys('og_quick_filters_keys')

	const optimizationStatus = useOptimizationStatus(optimizationGroupId)
	const canEditModelInput = useCanEditModelInput(optimizationGroupId)

	const isOgStatusLocked = optimizationStatus === OptimizationStatus.Locked
	const filtersSchema = schema.filter(isFilterbaleField) || []
	const [{ filters }, setQuery] = useLocationQuery()

	/**
	 * Apply quick filters to the url and navigate
	 */
	const applySelectedRules = (rules: FilterRuleModel[]) => {
		setQuery({
			filters: rules?.length ? rules : null,
		})
		// save recently used filter to local storage
		if (rules?.length) {
			localStorage.setItem(
				'recently_products_filter',
				encodeComplexQuery({ filters: rules }) as string
			)
		}
	}
	const enhancedDataChoices = useMemo(
		() => ({ ...dataChoices, pos_ids: posIds || [] }),
		[dataChoices, posIds]
	)
	const handleAssignToAnchorManagement = () => {
		ampli.tableAssignToAnchors()
		const selectedProductSkus = pipe(
			keys,
			map(pipe(split('__'), last))
		)(rowSelection)
		showModal('ASSIGN_TO_ANCHOR_MANAGEMENT_MODAL', {
			products: { [optimizationGroupId]: selectedProductSkus as string[] },
		})
		setRowSelection({})
	}

	return (
		<>
			<SavedReportPicker
				type={SavedFilterType.Products}
				onItemClick={(filterItem) => {
					ampli.savedFiltersClickOnFilter({ from: 'og assortment' })
					applySelectedRules(filterItem.filters)
				}}
			/>
			<TableFilters
				enableRecentFilters
				enableSaveFilters
				savedFiltersType={SavedFilterType.Products}
				onAnalyticsApply={() => ampli.ogAssortmentApplyFilters()}
				choices={enhancedDataChoices}
				schema={filtersSchema}
				filters={filters ?? []}
				onApply={applySelectedRules}
			/>
			<div className='flex items-center gap-2 px-1'>
				<Separator orientation='vertical' className='h-5!' />
				<QuickFiltersBar
					storedKeys={storedQuickFiltersKeys}
					onSaveStoredKeys={saveStoredQuickFiltersKeys}
					irremovableKeys={['category_ids', 'brands', 'pricing_campaign_id']}
					rules={filters ?? []}
					fieldsConfig={{
						...getFiledConfig(
							filtersSchema.map((schema) => toAdaptedSchema(schema, t)),
							enhancedDataChoices
						),
						...getFieldConfig(t),
					}}
					onApply={(rules) => {
						applySelectedRules(rules.map(formatFilterRule))
					}}
				/>
				<Separator orientation='vertical' className='h-5!' />
			</div>
			<ProductsBulkActionsDropdownMenu
				isBulkUpdateActionsDisabled={!canEditModelInput}
				onManualUpdateModal={onOpenBulkModal}
				onFileUpdateModal={onOpenImportModal}
			/>
			<ProductsAssignActionsDropdownMenu
				scope={{ optimization_group_id: optimizationGroupId }}
				isPricingCampaignsActionsDisabled={
					!keys(rowSelection).length || isOgStatusLocked
				}
				onSelectCampaign={onAssignToPricingCampaign}
				onCreateCampaign={onCreatePricingCampaign}
				onAssignToAnchorManagement={handleAssignToAnchorManagement}
				isAssignToAnchorManagementDisabled={keys(rowSelection)?.length > 20}
			/>
		</>
	)
}
